import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  IconButton,
  Alert,
  Snackbar,
  Collapse
} from '@mui/material';
import {
  Call,
  Mail,
  Map,
  Warning,
  Business,
  ContactPhone,
  LocalShipping,
  CalendarToday,
  Security,
  Assignment,
  ExpandLess,
  ExpandMore,
  ContentCopy
} from '@mui/icons-material';


const BatchResultsView = ({ data }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };


  const getProgressPercentage = () => {
    if (!data.totalSearched) return 0;
    return Math.round((data.processed / data.totalSearched) * 100);
  };

  return (
    <>

      <Card sx={{ mb: 2, background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(16,185,129,0.1) 100%)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {data.isComplete ? '✅' : '🔄'} Batch Search {data.isComplete ? 'Complete' : 'In Progress'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Processed {data.processed || 0} of {data.totalSearched} carriers • Found {data.successful} valid results
                {data.errors && data.errors.length > 0 && ` • ${data.errors.length} errors`}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" color="primary" fontWeight="bold">
                {getProgressPercentage()}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data.isComplete ? 'Complete' : 'Processing...'}
              </Typography>
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ width: '100%', height: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 4, overflow: 'hidden', mb: 2 }}>
            <Box
              sx={{
                height: '100%',
                background: data.isComplete ?
                  'linear-gradient(135deg, #10b981 0%, #059669 100%)' :
                  'linear-gradient(135deg, #6366f1 0%, #10b981 100%)',
                width: `${getProgressPercentage()}%`,
                transition: 'width 0.3s ease'
              }}
            />
          </Box>

          {/* Summary Chips */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Total: ${data.totalSearched}`} color="primary" variant="outlined" />
            <Chip label={`Found: ${data.successful}`} color="success" />
            <Chip
              label={`Remaining: ${data.totalSearched - (data.processed || 0)}`}
              color={data.isComplete ? "default" : "warning"}
            />
            {data.errors && data.errors.length > 0 && (
              <Chip label={`Not Found: ${data.errors.length}`} color="error" />
            )}
          </Box>
        </CardContent>
      </Card>


      <Box>
        <Typography variant="h6" gutterBottom sx={{
          color: data.isComplete ? '#10b981' : '#6366f1',
          mb: 2
        }}>
          {data.isComplete ? '✅' : '⏳'} Found Carriers ({data.successful})
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {data.batchResults && data.batchResults.map((carrier, index) => (
     
            <CarrierCard
              key={`carrier-${carrier.dotNumber || index}-${index}`}
              data={carrier}
            
              onToggleView={() => {}}
            />
          ))}

        
          {(!data.batchResults || data.batchResults.length === 0) && (
            <Card sx={{ textAlign: 'center', py: 4 }}>
              {data.isComplete ? (
                <>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Carriers Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No valid carriers found in the batch search
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    🔍 Searching...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Batch search is in progress. Results will appear here as they are found.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      Currently processing: {data.processed || 0} of {data.totalSearched}
                    </Typography>
                  </Box>
                </>
              )}
            </Card>
          )}
        </Box>
      </Box>

      {/* Completion Message */}
      {data.isComplete && (
        <Card sx={{ mt: 2, background: 'rgba(16, 185, 129, 0.1)', border: '1px solid #10b981' }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" color="#10b981" fontWeight="bold">
              ✅ Batch Search Completed Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Found {data.successful} carriers out of {data.totalSearched}
              {data.errors && data.errors.length > 0 && ` • ${data.errors.length} errors`}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
};

// Helper function to calculate active duration in years, months and days
const calculateActiveDuration = (activeDuration) => {
  if (!activeDuration || activeDuration === 'N/A') return 'N/A';
  
  // Extract years, months and days from active duration string
  const yearsMatch = activeDuration.match(/(\d+)\s*years?/);
  const monthsMatch = activeDuration.match(/(\d+)\s*months?/);
  const daysMatch = activeDuration.match(/(\d+)\s*days?/);
  
  let totalDays = 0;
  
  if (yearsMatch) {
    totalDays += parseInt(yearsMatch[1]) * 365;
  }
  
  if (monthsMatch) {
    totalDays += parseInt(monthsMatch[1]) * 30;
  }

  if (daysMatch) {
    totalDays += parseInt(daysMatch[1]);
  }
  
  // Convert total days to years, months, days
  if (totalDays > 0) {
    const years = Math.floor(totalDays / 365);
    const remainingDaysAfterYears = totalDays % 365;
    const months = Math.floor(remainingDaysAfterYears / 30);
    const days = remainingDaysAfterYears % 30;
    
    const parts = [];
    
    if (years > 0) {
      parts.push(`${years} year${years > 1 ? 's' : ''}`);
    }
    if (months > 0) {
      parts.push(`${months} month${months > 1 ? 's' : ''}`);
    }
    if (days > 0 || parts.length === 0) {
      parts.push(`${days} day${days > 1 ? 's' : ''}`);
    }
    
    return parts.join(' ');
  }
  
  return 'N/A';
};




const CarrierCard = ({ data, showBasicInfo, onToggleView }) => {
  const [localShowBasic, setLocalShowBasic] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [callStatus, setCallStatus] = useState({});
  const [emailStatus, setEmailStatus] = useState({});
  const [expandedCarriers, setExpandedCarriers] = useState({});

  // Decide effective showBasic mode:
  const effectiveShowBasic = typeof showBasicInfo === 'boolean' ? showBasicInfo : localShowBasic;
  const toggleLocalView = () => {
    if (typeof showBasicInfo === 'boolean') {
    
      if (onToggleView) onToggleView();
    } else {
      // internal per-card toggle
      setLocalShowBasic(prev => !prev);
    }
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleCopy = (text, label) => {
    if (!text || text === 'N/A') return;
    try {
      navigator.clipboard.writeText(text);
      showSnackbar(`${label} copied!`);
    } catch (e) {
      showSnackbar('Copy failed');
    }
  };

  const handleAction = (type, value, label) => {
    if (!value || value === 'N/A') return;

    switch (type) {
      case 'call':
        setCallStatus(prev => ({ ...prev, [value]: 'calling' }));
        showSnackbar(`Calling ${label}...`);
        const tel = value.replace(/\D/g, '');
        setTimeout(() => {
          window.open(`tel:${tel}`);
          setCallStatus(prev => ({ ...prev, [value]: 'called' }));
        }, 300);
        break;
      case 'email':
        setEmailStatus(prev => ({ ...prev, [value]: 'sending' }));
        showSnackbar(`Opening email for ${label}...`);
        setTimeout(() => {
          window.open(`mailto:${value}`);
          setEmailStatus(prev => ({ ...prev, [value]: 'sent' }));
        }, 300);
        break;
      case 'map':
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`, '_blank');
        showSnackbar(`Opening map for ${label}...`);
        break;
    }
  };

  const formatPhone = (phone) => {
    if (!phone || phone === 'N/A') return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  if (!data) {
    return (
      <Card sx={{ textAlign: 'center', py: 4, mb: 2 }}>
        <Typography variant="h6" color="text.secondary">
          Ready for search...
        </Typography>
      </Card>
    );
  }


  if (data.searchType === 'batch') {
    return <BatchResultsView data={data} />;
  }


const activeDuration = calculateActiveDuration(data.activeDuration);


if (effectiveShowBasic) {
  return (
    <>
      <Card sx={{
        background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(16,185,129,0.08) 100%)',
        border: '1px solid rgba(99,102,241,0.2)',
        borderRadius: 3,
        mb: 2,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(99,102,241,0.15)',
          border: '1px solid rgba(99,102,241,0.3)'
        }
      }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={1} alignItems="flex-start">
            {/* Left Content */}
            <Grid item xs={12} sm={8}>
              {/* Company Name */}
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                gutterBottom 
                sx={{ 
                  color: '#1f2937',
                  fontSize: '1.1rem',
                  lineHeight: 1.3,
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
                onClick={() => handleCopy(data.dbaName !== 'N/A' ? data.dbaName : data.legalName, 'Carrier Name')}
                style={{ cursor: 'pointer' }}
              >
                <Business sx={{ color: '#6366f1', fontSize: '1.2rem' }} />
                {data.dbaName !== 'N/A' ? data.dbaName : data.legalName}
              </Typography>

              {/* Status & DOT Chips */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                <Chip
                  icon={<Security sx={{ fontSize: '14px !important' }} />}
                  label={data.status}
                  color={data.status === 'Active' ? 'success' : 'error'}
                  size="small"
                  variant="filled"
                  sx={{ 
                    height: 28, 
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}
                />
                <Chip
                  icon={<Assignment sx={{ fontSize: '14px !important' }} />}
                  label={`USDOT: ${data.dotNumber}`}
                  variant="outlined"
                  size="small"
                  onClick={() => handleCopy(data.dotNumber, 'USDOT Number')}
                  sx={{ 
                    cursor: 'pointer', 
                    height: 28, 
                    fontSize: '0.75rem',
                    borderColor: '#6366f1',
                    color: '#6366f1',
                    '&:hover': { backgroundColor: 'rgba(99,102,241,0.04)' }
                  }}
                />
                {data.mcNumber !== 'N/A' && (
                  <Chip
                    label={`MC: ${data.mcNumber}`}
                    variant="outlined"
                    size="small"
                    onClick={() => handleCopy(data.mcNumber, 'MC Number')}
                    sx={{ 
                      cursor: 'pointer', 
                      height: 28, 
                      fontSize: '0.75rem',
                      borderColor: '#10b981',
                      color: '#10b981',
                      '&:hover': { backgroundColor: 'rgba(16,185,129,0.04)' }
                    }}
                  />
                )}
               {activeDuration && activeDuration !== 'N/A' && (
  <Chip
    label={`Active: ${activeDuration}`}
    variant="outlined"
    size="small"
    color="primary"
    sx={{ 
      cursor: 'pointer', 
                      height: 28, 
                      fontSize: '0.75rem',
      '& .MuiChip-label': { px: 0.5 }
    }}
  />
)}
              </Box>

          
<Box sx={{ 
  display: 'flex', 
  flexWrap: 'wrap', 
  gap: 1.5, 
  alignItems: 'center', 
  mb: 1
}}>
  {/* Contact Person */}
  {data.companyOfficer1 && data.companyOfficer1 !== 'N/A' && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <ContactPhone sx={{ fontSize: '0.9rem', color: '#10b981' }} />
      <Typography 
        variant="body2" 
        color="text.primary" 
        fontSize="0.75rem"
        fontWeight="medium"
        onClick={() => handleCopy(data.companyOfficer1, 'Contact Person')}
        sx={{ cursor: 'pointer', '&:hover': { color: '#10b981' } }}
      >
        {data.companyOfficer1}
      </Typography>
    </Box>
  )}

  {/* Truck Type */}
  {data.carrierOperation && data.carrierOperation !== 'N/A' && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <LocalShipping sx={{ fontSize: '0.9rem', color: '#8b5cf6' }} />
      <Typography 
        variant="body2" 
        color="text.primary" 
        fontSize="0.75rem"
        fontWeight="medium"
        onClick={() => handleCopy(data.carrierOperation, 'Truck Type')}
        sx={{ cursor: 'pointer', '&:hover': { color: '#8b5cf6' } }}
      >
        {data.carrierOperation}
      </Typography>
    </Box>
  )}

  {/* Power Units */}
  {data.powerUnits && data.powerUnits !== 'N/A' && data.powerUnits !== '0' && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Business sx={{ fontSize: '0.9rem', color: '#06b6d4' }} />
      <Typography 
        variant="body2" 
        color="text.primary" 
        fontSize="0.75rem"
        fontWeight="medium"
        onClick={() => handleCopy(data.powerUnits, 'Power Units')}
        sx={{ cursor: 'pointer', '&:hover': { color: '#06b6d4' } }}
      >
        {data.powerUnits} Trucks
      </Typography>
    </Box>
  )}

  {/* Phone */}
  {data.phone && data.phone !== 'N/A' && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Call sx={{ fontSize: '0.9rem', color: '#f59e0b' }} />
      <Typography 
        variant="body2" 
        color="text.primary" 
        fontSize="0.75rem"
        fontWeight="medium"
        onClick={() => handleCopy(formatPhone(data.phone), 'Phone')}
        sx={{ cursor: 'pointer', '&:hover': { color: '#f59e0b' } }}
      >
        {formatPhone(data.phone)}
      </Typography>
    </Box>
  )}

  {/* Email */}
  {data.email && data.email !== 'N/A' && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Mail sx={{ fontSize: '0.9rem', color: '#ef4444' }} />
      <Typography 
        variant="body2" 
        color="text.primary" 
        fontSize="0.75rem"
        fontWeight="medium"
        onClick={() => handleCopy(data.email, 'Email')}
        sx={{ 
          cursor: 'pointer', 
          '&:hover': { color: '#ef4444' },
          maxWidth: '300px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {data.email}
      </Typography>
    </Box>
  )}
</Box>
            
            </Grid>

            {/* Right Actions */}
            <Grid item xs={12} sm={4} sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: { sm: 'flex-end' },
              gap: 1.5
            }}>
              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                <IconButton
                  size="medium"
                  onClick={() => handleAction('call', data.phone, data.dbaName !== 'N/A' ? data.dbaName : data.legalName)}
                  disabled={data.phone === 'N/A'}
                  sx={{
                    background: callStatus[data.phone] === 'called' ? 
                      'linear-gradient(135deg, #059669 0%, #047857 100%)' : 
                      'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    '&:hover': { 
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      transform: 'translateY(-1px)'
                    },
                    width: 40,
                    height: 40,
                    boxShadow: '0 2px 8px rgba(16,185,129,0.3)'
                  }}
                >
                  <Call fontSize="small" />
                </IconButton>
                <IconButton
                  size="medium"
                  onClick={() => handleAction('email', data.email, data.dbaName !== 'N/A' ? data.dbaName : data.legalName)}
                  disabled={data.email === 'N/A'}
                  sx={{
                    background: emailStatus[data.email] === 'sent' ? 
                      'linear-gradient(135deg, #4338ca 0%, #3730a3 100%)' : 
                      'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
                    color: 'white',
                    '&:hover': { 
                      background: 'linear-gradient(135deg, #4338ca 0%, #3730a3 100%)',
                      transform: 'translateY(-1px)'
                    },
                    width: 40,
                    height: 40,
                    boxShadow: '0 2px 8px rgba(99,102,241,0.3)'
                  }}
                >
                  <Mail fontSize="small" />
                </IconButton>
              </Box>

              {/* View Details Button */}
              <Button
                variant="outlined"
                size="medium"
                onClick={toggleLocalView}
                startIcon={<ExpandMore />}
                sx={{
                  borderColor: '#6366f1',
                  color: '#6366f1',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  padding: '6px 16px',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(99,102,241,0.04)',
                    borderColor: '#4338ca',
                    color: '#4338ca',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                View Full Details
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}


  return (
    <Box>
      {/* Header Card */}
      <Card sx={{ mb: 2, background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(16,185,129,0.1) 100%)' }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={8}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#6366f1' }}>
                {data.dbaName !== 'N/A' ? data.dbaName : data.legalName}
              </Typography>
              {/* Primary Contact in Header */}
              {data.companyOfficer1 && data.companyOfficer1 !== 'N/A' && (
                <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                  Primary Contact: {data.companyOfficer1}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={4} sx={{ textAlign: { sm: 'right' } }}>
              <Button
                variant="outlined"
                size="small"
                onClick={toggleLocalView}
                sx={{ fontSize: '0.75rem' }}
              >
                Back to Basic
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Alerts Card */}
      {data.alerts && data.alerts.length > 0 && (
        <Card sx={{ mb: 2, border: '2px solid #f59e0b', background: 'rgba(245, 158, 11, 0.1)' }}>
          <CardContent sx={{ p: 1.5 }}>
            <Alert severity="warning" icon={<Warning />} sx={{ mb: 0 }}>
              <Box sx={{ maxHeight: '80px', overflowY: 'auto' }}>
                {data.alerts.map((alert, index) => (
                  <Typography key={index} variant="body2" fontSize="0.75rem">
                    • {alert}
                  </Typography>
                ))}
              </Box>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Main Content - Cards with internal 2 columns */}
      <Grid container spacing={1}>

        {/* Company & Contact Information Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Business sx={{ color: '#6366f1', fontSize: '1.2rem' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                  Company & Contact Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={6}>
                  <InfoItem label="Legal Name" value={data.legalName} onCopy={handleCopy} />
                  <InfoItem label="DBA Name" value={data.dbaName} onCopy={handleCopy} />
                  <InfoItem label="USDOT Number" value={data.dotNumber} onCopy={handleCopy} />
                  <InfoItem label="MC Number" value={data.mcNumber !== 'N/A' ? `MC-${data.mcNumber}` : 'N/A'} onCopy={handleCopy} />
                  <InfoItem label="Entity Type" value={data.entityType} onCopy={handleCopy} />
                  <InfoItem label="Business Type" value={data.businessOrganization} onCopy={handleCopy} />
                </Grid>

                {/* Right Column */}
                <Grid item xs={6}>
                  <InfoItem label="Primary Contact" value={data.companyOfficer1} onCopy={handleCopy} />
                  <InfoItem label="Secondary Contact" value={data.companyOfficer2} onCopy={handleCopy} />
                  <InfoItem
                    label="Email"
                    value={data.email}
                    onCopy={handleCopy}
                    actionable
                    actionType="email"
                    onAction={handleAction}
                    actionLabel={data.dbaName !== 'N/A' ? data.dbaName : data.legalName}
                  />
                  <InfoItem
                    label="Phone"
                    value={formatPhone(data.phone)}
                    onCopy={handleCopy}
                    actionable
                    actionType="call"
                    onAction={handleAction}
                    actionLabel={data.dbaName !== 'N/A' ? data.dbaName : data.legalName}
                  />
                  <InfoItem label="Fax" value={formatPhone(data.fax)} onCopy={handleCopy} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Operation & Status Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <LocalShipping sx={{ color: '#10b981', fontSize: '1.2rem' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                  Operation & Status
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={6}>
                  <InfoItem label="Carrier Operation" value={data.carrierOperation} onCopy={handleCopy} />
                  <InfoItem label="Status" value={data.status} status={data.status === 'Active' ? 'success' : 'error'} onCopy={handleCopy} />
                  <InfoItem label="Entity Classification" value={data.entityClassification} onCopy={handleCopy} />
                  <InfoItem label="Active Duration" value={activeDuration} onCopy={handleCopy} />
                </Grid>

                {/* Right Column */}
                <Grid item xs={6}>
                  <InfoItem label="MCS-150 Date" value={data.mcs150Date} onCopy={handleCopy} />
                  <InfoItem label="Add Date" value={data.addDate} onCopy={handleCopy} />
                  <InfoItem label="Mileage Year" value={data.mcs150MileageYear} onCopy={handleCopy} />
                  <InfoItem label="MCS-150 Mileage" value={data.mcs150Mileage} onCopy={handleCopy} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Fleet Information Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <LocalShipping sx={{ color: '#f59e0b', fontSize: '1.2rem' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                  Fleet Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={6}>
                  <InfoItem label="Total Drivers" value={data.totalDrivers} onCopy={handleCopy} />
                  <InfoItem label="CDL Drivers" value={data.totalCDLDrivers} onCopy={handleCopy} />
                  <InfoItem label="Power Units" value={data.powerUnits} onCopy={handleCopy} />
                </Grid>

                {/* Right Column - Vehicle Breakdown */}
                <Grid item xs={6}>
                  <Typography variant="body2" fontSize="0.75rem" fontWeight="bold" gutterBottom>
                    Vehicle Breakdown:
                  </Typography>

                  {/* Owned Vehicles */}
                  <Box sx={{ mb: 1.5, p: 1, background: 'rgba(16, 185, 129, 0.1)', borderRadius: 1 }}>
                    <Typography variant="body2" fontSize="0.7rem" fontWeight="bold" color="#10b981" gutterBottom>
                      🚛 Owned Vehicles:
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" fontSize="0.65rem" color="text.secondary">Tractors:</Typography>
                        <Typography variant="body2" fontSize="0.7rem" fontWeight="bold">{data.ownTractors}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" fontSize="0.65rem" color="text.secondary">Trailers:</Typography>
                        <Typography variant="body2" fontSize="0.7rem" fontWeight="bold">{data.ownTrailers}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" fontSize="0.65rem" color="text.secondary">Trucks:</Typography>
                        <Typography variant="body2" fontSize="0.7rem" fontWeight="bold">{data.ownTrucks}</Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Leased Vehicles */}
                  <Box sx={{ p: 1, background: 'rgba(99, 102, 241, 0.1)', borderRadius: 1 }}>
                    <Typography variant="body2" fontSize="0.7rem" fontWeight="bold" color="#6366f1" gutterBottom>
                      📋 Leased Vehicles:
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" fontSize="0.65rem" color="text.secondary">Tractors:</Typography>
                        <Typography variant="body2" fontSize="0.7rem" fontWeight="bold">{data.termLeasedTractors}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" fontSize="0.65rem" color="text.secondary">Trailers:</Typography>
                        <Typography variant="body2" fontSize="0.7rem" fontWeight="bold">{data.termLeasedTrailers}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" fontSize="0.65rem" color="text.secondary">Trucks:</Typography>
                        <Typography variant="body2" fontSize="0.7rem" fontWeight="bold">{data.termLeasedTrucks}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Address & Cargo Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <ContactPhone sx={{ color: '#8b5cf6', fontSize: '1.2rem' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                  Address & Cargo
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={6}>
                  <InfoItem
                    label="Physical Address"
                    value={data.physicalAddress}
                    onCopy={handleCopy}
                    actionable
                    actionType="map"
                    onAction={handleAction}
                    actionLabel="Physical Address"
                  />
                  <InfoItem
                    label="Mailing Address"
                    value={data.mailingAddress !== data.physicalAddress ? data.mailingAddress : 'Same as Physical'}
                    onCopy={handleCopy}
                    actionable
                    actionType="map"
                    onAction={handleAction}
                    actionLabel="Mailing Address"
                  />
                  <InfoItem label="Cell Phone" value={formatPhone(data.cellPhone)} onCopy={handleCopy} />
                </Grid>

                {/* Right Column */}
                <Grid item xs={6}>
                  <InfoItem label="Cargo Classification" value={data.cargoClassification} onCopy={handleCopy} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Authority & Insurance Cards */}
        {data.authorityInfo && (
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Security sx={{ color: '#ef4444', fontSize: '1.2rem' }} />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                    Authority Information
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InfoItem
                      label="Common Authority"
                      value={data.authorityInfo.commonAuthority}
                      status={data.authorityInfo.commonAuthority === 'Active' ? 'success' : 'error'}
                      onCopy={handleCopy}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoItem
                      label="Broker Authority"
                      value={data.authorityInfo.brokerAuthority}
                      status={data.authorityInfo.brokerAuthority === 'Active' ? 'success' : 'warning'}
                      onCopy={handleCopy}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {data.insuranceInfo && (
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Assignment sx={{ color: '#06b6d4', fontSize: '1.2rem' }} />
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }} fontSize="0.9rem">
                    Insurance Information
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <InfoItem label="Insurance Type" value={data.insuranceInfo.type} onCopy={handleCopy} />
                    <InfoItem label="Policy Number" value={data.insuranceInfo.policyNumber} onCopy={handleCopy} />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoItem label="Coverage Amount" value={data.insuranceInfo.maxCoverage} onCopy={handleCopy} />
                    <InfoItem label="Insurance Company" value={data.insuranceInfo.companyName} onCopy={handleCopy} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* External Links Card */}
      <Card sx={{ mt: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom fontSize="0.8rem">
            Additional Resources
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.open(`https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=USDOT&query_string=${data.dotNumber}`, '_blank')}
              sx={{ fontSize: '0.7rem' }}
            >
              SAFER Snapshot
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.open(`https://li-public.fmcsa.dot.gov/LIVIEW/pkg_carrquery.prc_carrlist?cos_seq_no=${data.dotNumber}`, '_blank')}
              sx={{ fontSize: '0.7rem' }}
            >
              Licensing & Insurance
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

// Info Item Component
const InfoItem = ({ label, value, status, actionable, actionType, actionLabel, onCopy, onAction }) => (
  <Box sx={{ mb: 1.2 }}>
    <Typography variant="body2" color="text.secondary" fontSize="0.7rem" gutterBottom>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {status ? (
        <Chip
          label={value}
          color={status}
          size="small"
          variant="filled"
          sx={{ height: 20, fontSize: '0.6rem' }}
        />
      ) : (
        <Typography
          variant="body2"
          fontSize="0.75rem"
          fontWeight="bold"
          onClick={() => value && value !== 'N/A' && onCopy(value, label)}
          sx={{
            cursor: value && value !== 'N/A' ? 'pointer' : 'default',
            '&:hover': value && value !== 'N/A' ? { color: '#6366f1' } : {}
          }}
        >
          {value || 'N/A'}
        </Typography>
      )}
      {value && value !== 'N/A' && (
        <>
          <IconButton
            size="small"
            onClick={() => onCopy(value, label)}
            sx={{ width: 16, height: 16, color: '#6b7280' }}
          >
            <ContentCopy fontSize="inherit" />
          </IconButton>
          {actionable && (
            <IconButton
              size="small"
              onClick={() => onAction(actionType, value, actionLabel)}
              sx={{
                width: 16,
                height: 16,
                color: actionType === 'call' ? '#10b981' : actionType === 'email' ? '#6366f1' : '#f59e0b'
              }}
            >
              {actionType === 'call' && <Call fontSize="inherit" />}
              {actionType === 'email' && <Mail fontSize="inherit" />}
              {actionType === 'map' && <Map fontSize="inherit" />}
            </IconButton>
          )}
        </>
      )}
    </Box>
  </Box>
);

export default CarrierCard;